import { useCallback, useEffect, useRef, useState } from 'react'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { fetchOperatingCostSalePostings } from '@/lib/api/operating-costs-admin.service'
import type { OperatingCostSalePostingsPage } from '@/types/api'
import {
  OPERATING_COSTS_LOAD_ERROR,
  resolveOperatingCostSalePostingsPage,
  resolveOperatingCostsError,
} from '../lib/operating-costs'

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true
  if (error instanceof Error && (error.name === 'AbortError' || /abort/i.test(error.message))) {
    return true
  }
  return false
}

export type WhatsAppApiCostFilterValues = {
  campaignId: string
  month: string
  from: string
  to: string
}

const EMPTY_FILTERS: WhatsAppApiCostFilterValues = {
  campaignId: '',
  month: '',
  from: '',
  to: '',
}

export type WhatsAppApiCostPostingsController = {
  data: OperatingCostSalePostingsPage | null
  isLoading: boolean
  error: string | null
  draftFilters: WhatsAppApiCostFilterValues
  setDraftFilters: (filters: WhatsAppApiCostFilterValues) => void
  applyFilters: () => void
  clearFilters: () => void
  goToPage: (page: number) => void
}

export function useWhatsAppApiCostPostings(categoryId: number): WhatsAppApiCostPostingsController {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const runWithStepUpRef = useRef(runWithStepUp)
  runWithStepUpRef.current = runWithStepUp
  const [data, setData] = useState<OperatingCostSalePostingsPage | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [draftFilters, setDraftFilters] = useState<WhatsAppApiCostFilterValues>(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState<WhatsAppApiCostFilterValues>(EMPTY_FILTERS)
  const [page, setPage] = useState(1)
  const loadControllerRef = useRef<AbortController | null>(null)

  const load = useCallback(
    async (filters: WhatsAppApiCostFilterValues, pageNumber: number) => {
      loadControllerRef.current?.abort()
      const controller = new AbortController()
      loadControllerRef.current = controller
      setIsLoading(true)
      setError(null)
      try {
        const campaignId = Number(filters.campaignId)
        const response = await runWithStepUpRef.current(() =>
          fetchOperatingCostSalePostings(
            categoryId,
            {
              campaignId: Number.isInteger(campaignId) && campaignId > 0 ? campaignId : null,
              month: filters.month,
              from: filters.from,
              to: filters.to,
              page: pageNumber,
            },
            controller.signal
          )
        )
        if (controller.signal.aborted) return
        const resolved = resolveOperatingCostSalePostingsPage(response)
        if (!resolved) {
          setData(null)
          setError(OPERATING_COSTS_LOAD_ERROR)
          return
        }
        setData(resolved)
      } catch (err) {
        if (controller.signal.aborted || isAbortError(err)) return
        setData(null)
        setError(resolveOperatingCostsError(err))
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    },
    [categoryId]
  )

  useEffect(() => {
    void load(appliedFilters, page)
    return () => {
      loadControllerRef.current?.abort()
    }
  }, [appliedFilters, load, page])

  const applyFilters = useCallback(() => {
    setPage(1)
    setAppliedFilters(draftFilters)
  }, [draftFilters])

  const clearFilters = useCallback(() => {
    setDraftFilters(EMPTY_FILTERS)
    setAppliedFilters(EMPTY_FILTERS)
    setPage(1)
  }, [])

  const goToPage = useCallback((nextPage: number) => {
    if (nextPage < 1) return
    setPage(nextPage)
  }, [])

  return {
    data,
    isLoading,
    error,
    draftFilters,
    setDraftFilters,
    applyFilters,
    clearFilters,
    goToPage,
  }
}
